import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { SharedService } from '../shared.service';
import { ApiService } from '../api.service';
import Papa from "papaparse";
import * as XLSX from 'xlsx';
import { forkJoin, of } from 'rxjs';
import moment from 'moment';

@Component({
  selector: 'app-tlo-data',
  templateUrl: './tlo-data.component.html',
  styleUrl: './tlo-data.component.scss'
})
export class TloDataComponent {
  extractedData: any = null;
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('olbFileInput') olbFileInput!: ElementRef;
  @Input() qr_from: any;
  @Output() tloResponse: EventEmitter<any> = new EventEmitter();
  @Output() cancelUpload: EventEmitter<any> = new EventEmitter();
  tableHeaders = {
    personalInfo: ["record_id", "firstname", "lastname", "Date of Birth", "Social Security Number"],
    addresses: ["Address"],
    contactInfo: ["Phone", "Email"],
    matchingScoringInfo: ["Match Codes", "Score"]
  };
  categorizedData!: {
    personalInfo: any,
    addresses: [];
    phoneInfo: [];
    emailInfo: [];
    bankruptcyInfo: [];
  };
  tloFileName = '';

  olbExtractedData: any = null;
  olbCategorizedData: { records: any[] } | null = null;
  olbFileName = '';

  notes = '';
  dataSubmitted = false;
  isTloDragOver = false;
  isOlbDragOver = false;

  private readonly accountIdAliases = [
    'record_id', 'account id', 'account_id', 'reference_nbr', 'reference nbr'
  ];
  private readonly scoreAliases = [
    'recovery score', 'recovery_score', 'recuvery_score', 'score_1', 'score'
  ];

  constructor(private _shared: SharedService, private _api: ApiService) {}

  private isEmpty(value: any): boolean {
    return value === undefined || value === null || value === '' ||
      (typeof value === 'string' && value.trim() === '');
  }

  private getRowValueByAliases(row: any, aliases: string[]): any {
    if (!row) return null;
    const keyMap: { [normalized: string]: string } = {};
    Object.keys(row).forEach((key) => {
      keyMap[key.trim().toLowerCase()] = key;
    });
    for (const alias of aliases) {
      const originalKey = keyMap[alias.toLowerCase()];
      if (originalKey !== undefined && !this.isEmpty(row[originalKey])) {
        return row[originalKey];
      }
    }
    return null;
  }

  private normalizeRowsForTlo(data: any[]): any[] {
    return (data || []).map((row) => {
      if (!row) return row;
      const normalized = { ...row };
      const accountId = this.getRowValueByAliases(row, this.accountIdAliases);
      if (!this.isEmpty(accountId)) {
        normalized.record_id = accountId;
      }
      const score = this.getRowValueByAliases(row, this.scoreAliases);
      if (!this.isEmpty(score)) {
        normalized.recuvery_score = score;
      }
      return normalized;
    });
  }

  private normalizeRowsForOlb(data: any[]): any[] {
    return (data || []).map((row) => {
      if (!row) return row;
      const normalized = { ...row };
      const accountId = this.getRowValueByAliases(row, this.accountIdAliases);
      if (!this.isEmpty(accountId)) {
        normalized.Reference_Nbr = accountId;
      }
      const score = this.getRowValueByAliases(row, this.scoreAliases);
      if (!this.isEmpty(score)) {
        normalized.recuvery_score = score;
      }
      return normalized;
    });
  }

  private hasValidAccountId(rows: any[], field: 'record_id' | 'Reference_Nbr'): boolean {
    return rows.some((row) => row && !this.isEmpty(row[field]));
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    this.handleTloFile(file);
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onTloDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isTloDragOver = true;
  }

  onTloDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isTloDragOver = false;
  }

  onTloDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isTloDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleTloFile(file);
    }
  }

  private handleTloFile(file: File | undefined | null) {
    if (!file) return;

    const reader = new FileReader();
    const fileName = file.name.toLowerCase();
    const isXlsx = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    const isJson = file.type === "application/json" || fileName.endsWith('.json');

    reader.onload = (e: any) => {
      try {
        const fileContent = e.target.result;
        let parsedData: any = [];
        if (isXlsx) {
          parsedData = this.parseXLSX(fileContent);
        } else if (isJson) {
          parsedData = JSON.parse(fileContent);
        } else {
          parsedData = this.parseCSV(fileContent);
        }

        parsedData = this.normalizeRowsForTlo(parsedData);

        if (parsedData && parsedData.length > 0) {
          const hasRecordId = this.hasValidAccountId(parsedData, 'record_id');
          if (!hasRecordId) {
            this._shared.customPopups("Invalid TLO file format. Please upload a valid TLO file.", 1);
            if (this.fileInput) this.fileInput.nativeElement.value = '';
            return;
          }
        } else {
          this._shared.customPopups("The uploaded TLO file is empty or invalid.", 1);
          if (this.fileInput) this.fileInput.nativeElement.value = '';
          return;
        }

        this.extractedData = parsedData;
        this.categorizedData = this._shared.categorizeFields(this.extractedData);
        this.tloFileName = file.name;
        console.log(this.categorizedData);
      } catch (error) {
        console.error("Error reading file:", error);
        this._shared.customPopups("Unable to read the selected file. Please check the format.", 1);
      }
    };

    if (isXlsx) {
      reader.readAsBinaryString(file);
    } else {
      reader.readAsText(file);
    }
  }

  onOlbFileSelected(event: any) {
    const file: File = event.target.files[0];
    this.handleOlbFile(file);
    if (this.olbFileInput) {
      this.olbFileInput.nativeElement.value = '';
    }
  }

  onOlbDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isOlbDragOver = true;
  }

  onOlbDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isOlbDragOver = false;
  }

  onOlbDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isOlbDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleOlbFile(file);
    }
  }

  private handleOlbFile(file: File | undefined | null) {
    if (!file) return;

    const reader = new FileReader();
    const fileName = file.name.toLowerCase();
    const isXlsx = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    const isJson = file.type === "application/json" || fileName.endsWith('.json');

    reader.onload = (e: any) => {
      try {
        const fileContent = e.target.result;
        let parsedData: any = [];
        if (isXlsx) {
          parsedData = this.parseXLSX(fileContent);
        } else if (isJson) {
          parsedData = JSON.parse(fileContent);
        } else {
          parsedData = this.parseCSV(fileContent);
        }

        parsedData = this.normalizeRowsForOlb(parsedData);

        if (parsedData && parsedData.length > 0) {
          const hasReferenceNbr = this.hasValidAccountId(parsedData, 'Reference_Nbr');
          if (!hasReferenceNbr) {
            this._shared.customPopups("Invalid OLB file format. Please upload a valid OLB file.", 1);
            if (this.olbFileInput) this.olbFileInput.nativeElement.value = '';
            return;
          }
        } else {
          this._shared.customPopups("The uploaded OLB file is empty or invalid.", 1);
          if (this.olbFileInput) this.olbFileInput.nativeElement.value = '';
          return;
        }

        this.olbExtractedData = parsedData;
        this.olbCategorizedData = this._shared.categorizeOlbFields(this.olbExtractedData);
        this.olbFileName = file.name;
        console.log(this.olbCategorizedData);
      } catch (error) {
        console.error("Error reading OLB file:", error);
        this._shared.customPopups("Unable to read the selected file. Please check the format.", 1);
      }
    };

    if (isXlsx) {
      reader.readAsBinaryString(file);
    } else {
      reader.readAsText(file);
    }
  }

  parseCSV(csvString: string) {
    if (!csvString.trim()) return [];

    const parsed = Papa.parse(csvString, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
    });

    if (parsed.errors.length) {
      console.error("CSV Parsing Errors:", parsed.errors);
    }
    return parsed.data;
  }

  parseXLSX(binaryStr: string): any[] {
    if (!binaryStr) return [];

    const workbook = XLSX.read(binaryStr, { type: 'binary', cellDates: true });

    const rows = workbook.SheetNames.flatMap((sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      if (!sheet) return [];
      return XLSX.utils.sheet_to_json(sheet, { defval: null, raw: true }) as any[];
    });

    return rows.map((row) => this.formatDateFields(row));
  }

  private formatDateFields(row: any): any {
    const formatted = { ...row };
    Object.keys(formatted).forEach((key) => {
      if (formatted[key] instanceof Date) {
        formatted[key] = this.formatDateAsMDY(formatted[key]);
      }
    });
    return formatted;
  }

  private formatDateAsMDY(date: Date): string {
    return moment.utc(date).format('MM/DD/YYYY');
  }

  removeFile() {
    this.categorizedData = {
      personalInfo: {},
      addresses: [],
      phoneInfo: [],
      emailInfo: [],
      bankruptcyInfo: []
    };
    this.extractedData = null;
    this.tloFileName = '';
  }

  removeOlbFile() {
    this.olbCategorizedData = null;
    this.olbExtractedData = null;
    this.olbFileName = '';
  }

  cancel() {
    this.cancelUpload.emit(true);
  }

  hasTloData(): boolean {
    return !!(this.categorizedData && this.categorizedData.addresses && this.categorizedData.addresses.length);
  }

  hasOlbData(): boolean {
    return !!(this.olbCategorizedData && this.olbCategorizedData.records && this.olbCategorizedData.records.length);
  }

  triggerTloFileInput() {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  triggerOlbFileInput() {
    if (this.olbFileInput) {
      this.olbFileInput.nativeElement.click();
    }
  }

  submitFile() {
    if (!this.notes.trim()) {
      this._shared.customPopups("Please enter notes", 1);
      return;
    }

    const hasTlo = this.hasTloData();
    const hasOlb = this.hasOlbData();

    if (!hasTlo && !hasOlb) {
      this._shared.customPopups("Please upload at least one file", 1);
      return;
    }

    const tloCall$ = hasTlo
      ? this._api.api(
          {
            addresses: this.categorizedData.addresses,
            phoneInfo: this.categorizedData.phoneInfo,
            emailInfo: this.categorizedData.emailInfo,
            bankruptcyInfo: this.categorizedData.bankruptcyInfo,
            personal_info: this.categorizedData.personalInfo,
            notes: this.notes,
            qr_from: this.qr_from
          },
          'finance/add_alternate_bulk_details'
        )
      : of(null);

    const olbCall$ = hasOlb
      ? this._api.api(
          {
            records: this.olbCategorizedData!.records,
            notes: this.notes,
            qr_from: this.qr_from,
            is_export: 1
          },
          'finance/upload_olb_file'
        )
      : of(null);

    this.dataSubmitted = true;

    forkJoin([tloCall$, olbCall$]).subscribe({
      next: (responses) => {
        console.log(responses);
        this.dataSubmitted = false;
        const hasFailure = responses.some((res: any) => res === false || (res && res.is_error));
        this.tloResponse.emit(!hasFailure);
      },
      error: (err) => {
        console.error('Upload error:', err);
        this.dataSubmitted = false;
        this.tloResponse.emit(false);
      }
    });
  }
}

interface Address {
  Line1?: string;
  City?: string;
  State?: string;
  Zip?: string;
}
